import API_END_POINTS from '../../../api/endpoints';
import { dataFetch } from '../../../api/data-fetch';

// initial state
const state = () => ({
  eventData: null,
  // horse, harness or greyhound
  raceType: '',
  meetingName: '',
  selectionData: [],
  selectionModalData: [],
  selectionModalActive: false,
  selectionModalTabIndex: 0,
  blackbookModalActive: false,
  bestWinOdds: [],
  bestPlaceOdds: [],
  eventWinOdds: [],
  eventPlaceOdds: [],
  eventLiveBets: [],
});

// actions
const actions = {
  async fetchEventById(state, { eventId, raceType }, fetcher = dataFetch) {
    try {
      // set race type
      state.commit('SET_RACE_TYPE', raceType);

      let eventEndpoint = '';
      // HARNESS EVENT API SET UP
      if (raceType === 'harness') {
        const includeString = 'entry_conditions,prize_money,results,venue,weather,selections';
        eventEndpoint = `${API_END_POINTS.HARNESS.EVENTS}/${eventId}?include=${includeString}`;
      }

      // HORSE EVENT API SET UP
      if (raceType === 'horse') {
        eventEndpoint = `${API_END_POINTS.RACING.EVENT}&eventId=${eventId}`;
      }

      const res = await fetcher(eventEndpoint);
      const eventData = res.data.event;
      state.commit('SET_EVENT_DATA', { eventData });

      const { selections } = eventData;

      if (Array.isArray(selections) && selections.length > 0) {
        const selectionsWithStats = await getSelectionStats(selections, eventId, raceType);
        state.commit('SET_SELECTION_DATA', selectionsWithStats);
        const selectionsWithOdds = await getSelectionOdds(selectionsWithStats, eventId);
        state.commit('SET_SELECTION_DATA', selectionsWithOdds);
      }
    } catch (error) {
      console.log(error);
    }
  },

  async fetchEventBySlug(state, { slug, eventNumber, raceType }, fetcher = dataFetch) {
    try {
      // set race type
      state.commit('SET_RACE_TYPE', raceType);

      let meetingEndpoint = '';
      if (raceType === 'harness') {
        meetingEndpoint = `${API_END_POINTS.HARNESS.MEETING}${slug}?include=events`;
      }

      const res = await fetcher(meetingEndpoint);
      const meetingData = res.data.meeting;
      state.commit('SET_MEETING_NAME', meetingData.name);

      const event = meetingData.events.find(item => item.event_number == eventNumber);

      if (event) {
        let eventEndpoint = '';

        // HARNESS EVENT API SET UP
        if (raceType === 'harness') {
          const includeString = 'entry_conditions,prize_money,results,venue,weather,selections';
          eventEndpoint = `${API_END_POINTS.HARNESS.EVENTS}/${event.id}?include=${includeString}`;
        }

        // HORSE EVENT API SET UP
        if (raceType === 'horse') {
          eventEndpoint = `${API_END_POINTS.RACING.EVENT}&eventId=${event.id}`;
        }

        const res = await fetcher(eventEndpoint);
        const eventData = res.data.event;
        state.commit('SET_EVENT_DATA', { eventData });

        const { selections } = eventData;

        if (Array.isArray(selections) && selections.length > 0) {
          const selectionsWithStats = await getSelectionStats(selections, event.id, raceType);
          state.commit('SET_SELECTION_DATA', selectionsWithStats);
          const selectionsWithOdds = await getSelectionOdds(selectionsWithStats, event.id);
          state.commit('SET_SELECTION_DATA', selectionsWithOdds);
        }
      }
    } catch (error) {
      console.log(error);
    }
  },

  setMeetingName(state, meetingName) {
    state.commit('SET_MEETING_NAME', meetingName);
  },
  async setSelectionModalData(state, selectionData) {
    const records = await fetchCompetitorFullFormRecords(selectionData.competitor_id);
    selectionData.records = records;
    state.commit('SET_SELECTION_MODAL_DATA', selectionData);
  },
  toggleSelectionModal(state, tabIndex) {
    state.commit('SET_SELECTION_MODAL_TAB_INDEX', { tabIndex });
    state.commit('TOGGLE_SELECTION_MODAL');
  },
  toggleBlackbookModal(state) {
    state.commit('TOGGLE_BLACKBOOK_MODAL');
  },
  async updateModalData({ state, commit }, { selectionId, direction }) {
    let index = state.selectionData.findIndex(selection => selection.id === selectionId);

    if (direction === 'left') {
      index += -1;
    }
    if (direction === 'right') {
      index += 1;
    }
    // mobile modal not available for scratched selections
    if (!state.selectionData[index].isScratched) {
      const records = await fetchCompetitorFullFormRecords(state.selectionData[index].competitor_id);
      state.selectionData[index].records = records;
      commit('SET_SELECTION_MODAL_TAB_INDEX', 0);
      commit('SET_SELECTION_MODAL_DATA', state.selectionData[index]);
    } else {
      if (direction === 'left') {
        for (let i = index; i >= 0; i--) {
          if (!state.selectionData[i].isScratched) {
            const records = await fetchCompetitorFullFormRecords(state.selectionData[i].competitor_id);
            state.selectionData[i].records = records;
            commit('SET_SELECTION_MODAL_TAB_INDEX', 0);
            commit('SET_SELECTION_MODAL_DATA', state.selectionData[i]);
            return;
          }
        }
      }

      if (direction === 'right') {
        for (let i = index; i <= state.selectionData.length - 1; i++) {
          if (!state.selectionData[i].isScratched) {
            const records = await fetchCompetitorFullFormRecords(state.selectionData[i].competitor_id);
            state.selectionData[i].records = records;
            commit('SET_SELECTION_MODAL_TAB_INDEX', 0);
            commit('SET_SELECTION_MODAL_DATA', state.selectionData[i]);
            return;
          }
        }
      }
    }
  },
};

function isSelectionScratched(selection) {
  return selection.status === 'scratched' || selection.barrier_number === 'Scr';
}

async function getOdds(eventId, paramString, fetcher = dataFetch) {
  const oddsEndpoint = `${API_END_POINTS.ODDS.EVENT_BASE}${eventId}?${paramString}`;
  const oddsData = await fetcher(oddsEndpoint);
  return oddsData;
}

// fetch full form records by competitor id
async function fetchCompetitorFullFormRecords(competitorId, fetcher = dataFetch) {
  try {
    const includeString = 'results,venue,prize_money,entry_conditions';
    const recordsEndpoint = `${API_END_POINTS.HARNESS.EVENTS}?include=${includeString}&competitorId=${competitorId}`;
    const res = await fetcher(recordsEndpoint);
    const competitorRecords = res.data.events;
    return competitorRecords;
  } catch (error) {
    console.log(error);
  }
}

async function getStatsByEvent(eventId, fetcher = dataFetch) {
  try {
    const statsEndpoint = `${API_END_POINTS.HARNESS.STATS}?events=${eventId}`;
    const statsData = await fetcher(statsEndpoint);
    return statsData.data;
  } catch (error) {
    console.log(error);
  }
}

async function getSelectionStats(selections, eventId, raceType) {
  let statsByEvent = {};
  const isHarness = raceType === 'harness';

  if (isHarness) {
    statsByEvent = await getStatsByEvent(eventId);
  }

  const result = selections.map(item => {
    const stats = isHarness ? statsByEvent.stats.find(stat => stat.selection_id === item.id) : item.stats;

    return {
      ...item,
      stats,
      bookmakerWinOdds: [],
      bookmakerPlaceOdds: [],
      bestWinOdds: [],
      bestPlaceOdds: [],
      averageWinOdds: [],
      averagePlaceOdds: [],
      isScratched: isSelectionScratched(item),
    };
  });

  return result;
}

async function getSelectionOdds(selections, eventId) {
  const oddsInclueString = 'betTypes=fixed-win,fixed-place&priceFluctuations=50&type=bookmaker,best,average';
  const oddsData = await getOdds(eventId, oddsInclueString);

  const result = selections.map(item => {
    const bookmakerWinOdds = oddsData.odds.filter(
      odds => odds.betType === 'fixed-win' && odds.type === 'bookmaker' && item.id === odds.selectionId
    );
    const bookmakerPlaceOdds = oddsData.odds.filter(
      odds => odds.betType === 'fixed-place' && odds.type === 'bookmaker' && item.id === odds.selectionId
    );

    const bestWinOdds = oddsData.odds.filter(
      odds => odds.betType === 'fixed-win' && odds.type === 'best' && item.id === odds.selectionId
    );
    const bestPlaceOdds = oddsData.odds.filter(
      odds => odds.betType === 'fixed-place' && odds.type === 'best' && item.id === odds.selectionId
    );

    const averageWinOdds = oddsData.odds.filter(
      odds => odds.betType === 'fixed-win' && odds.type === 'average' && item.id === odds.selectionId
    );
    const averagePlaceOdds = oddsData.odds.filter(
      odds => odds.betType === 'fixed-place' && odds.type === 'average' && item.id === odds.selectionId
    );

    return {
      ...item,
      bookmakerWinOdds,
      bookmakerPlaceOdds,
      bestWinOdds,
      bestPlaceOdds,
      averageWinOdds,
      averagePlaceOdds,
    };
  });

  return result;
}

// mutations
const mutations = {
  SET_RACE_TYPE: (state, raceType) => {
    state.raceType = raceType;
  },
  SET_MEETING_NAME: (state, meetingName) => {
    state.meetingName = meetingName;
  },
  SET_EVENT_DATA: (state, { eventData }) => {
    state.eventData = eventData;
  },
  SET_SELECTION_DATA: (state, selections) => {
    state.selectionData = selections;
  },
  SET_SELECTION_MODAL_DATA: (state, modalData) => {
    state.selectionModalData = modalData;
  },
  TOGGLE_SELECTION_MODAL: state => {
    state.selectionModalActive = !state.selectionModalActive;
  },
  TOGGLE_BLACKBOOK_MODAL: state => {
    state.blackbookModalActive = !state.blackbookModalActive;
  },
  SET_COMPETITOR_FULL_FORM_RECORDS: (state, { competitorRecords }) => {
    state.selectionModalData.records = competitorRecords;
  },
  SET_SELECTION_MODAL_TAB_INDEX: (state, { tabIndex }) => {
    state.selectionModalTabIndex = tabIndex;
  },
};

// getter
const getters = {
  getEventData: state => {
    return state.eventData;
  },
  getRaceType: state => {
    return state.raceType;
  },
  getMeetingName: state => {
    return state.eventData ? `${state.meetingName} R${state.eventData.event_number}` : '';
  },
  getSelections: state => {
    return state.selectionData;
  },
  isFirstSelection: state => {
    const index = state.selectionData.findIndex(selection => selection.id === state.selectionModalData.id);
    return index === 0;
  },
  isLastSelection: state => {
    const index = state.selectionData.findIndex(selection => selection.id === state.selectionModalData.id);
    return index === state.selectionData.length - 1;
  },
  getSelectionModalData: state => {
    return state.selectionModalData;
  },
  getSelectionModalStatus: state => {
    return state.selectionModalActive;
  },
  getBlackbookModalStatus: state => {
    return state.blackbookModalActive;
  },
  getLiveBets: state => {
    return state.eventLiveBets;
  },
  getSelectionModalTabIndex: state => {
    return state.selectionModalTabIndex;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
